import React, { useState, useRef, useEffect, useCallback } from 'react';
import './OrganizationPayout.css';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Popover,
  Select,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EditTransactionHistory from './EditTransactionHistory';
import ReactDOM from 'react-dom';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';
import usePagination from '@mui/material/usePagination';

const PayoutTableSkeleton = () => {
  return (
    <Box className="org-transaction-table-container">
      <table className="org-payout-skeleton-table">
        <thead>
          <tr>
            <th className="org-payout-skeleton-th">
              <Box className="org-payout-skeleton-block header-skeleton" />
            </th>
            <th className="org-payout-skeleton-th">
              <Box className="org-payout-skeleton-block header-skeleton" />
            </th>
            <th className="org-payout-skeleton-th">
              <Box className="org-payout-skeleton-block header-skeleton" />
            </th>
            <th className="org-payout-skeleton-th">
              <Box className="org-payout-skeleton-block header-skeleton" />
            </th>
            <th className="org-payout-skeleton-th">
              <Box className="org-payout-skeleton-block header-skeleton" />
            </th>
            <th className="org-payout-skeleton-th">
              <Box className="org-payout-skeleton-block header-skeleton" />
            </th>
            <th className="org-payout-skeleton-th">
              <Box className="org-payout-skeleton-block header-skeleton" />
            </th>
          </tr>
        </thead>
        <tbody>
          {[...Array(8)].map((_, index) => (
            <tr key={index} className="org-payout-skeleton-row">
              <td className="org-payout-skeleton-td">
                <Box className="org-payout-skeleton-block text-skeleton" />
              </td>
              <td className="org-payout-skeleton-td">
                <Box className="org-payout-skeleton-block text-skeleton" />
              </td>
              <td className="org-payout-skeleton-td">
                <Box className="org-payout-skeleton-block text-skeleton" />
              </td>
              <td className="org-payout-skeleton-td">
                <Box className="org-payout-skeleton-block text-skeleton" />
              </td>
              <td className="org-payout-skeleton-td">
                <Box className="org-payout-skeleton-block text-skeleton" />
              </td>
              <td className="org-payout-skeleton-td">
                <Box className="org-payout-skeleton-block text-skeleton" />
              </td>
              <td className="org-payout-skeleton-td">
                <Box className="org-payout-skeleton-block button-skeleton" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

const OrganizationPayout = ({ organizationId }) => {
  const [activeFilter, setActiveFilter] = useState('this-month');
  const [openEditModal, setOpenEditModal] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Calendar navigation state
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Custom calendar state
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedDateRange, setSelectedDateRange] = useState({ start: null, end: null });
  const [isSelectingStart, setIsSelectingStart] = useState(true);
  const customButtonRef = useRef(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // RBAC manage payouts permission
  const [canManagePayouts, setCanManagePayouts] = useState(false);

  useEffect(() => {
    let mounted = true;
    const fetchMe = async () => {
      try {
        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        const perms = Array.isArray(data?.permissions) ? data.permissions : [];
        if (mounted) setCanManagePayouts(perms.includes('manage_payouts'));
      } catch (_) {
        if (mounted) setCanManagePayouts(false);
      }
    };
    fetchMe();
    return () => {
      mounted = false;
    };
  }, []);

  // Helper to format date as YYYY-MM-DD
  const formatDateForAPI = date => {
    if (!date) return '';
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
  };

  // Helper to get date X days/months/years ago
  const getDateAgo = (unit, value) => {
    const d = new Date();
    if (unit === 'days') d.setDate(d.getDate() - value);
    if (unit === 'months') d.setMonth(d.getMonth() - value);
    if (unit === 'years') d.setFullYear(d.getFullYear() - value);
    return d;
  };

  // Fetch transaction history with date range
  const fetchTransactionHistory = useCallback(
    async (startDate, endDate, customPage = page, customRowsPerPage = rowsPerPage) => {
      if (!organizationId) {
        setError('Organization ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        let url = `/admin/organisation/transactions/${organizationId}`;
        const params = [];
        if (startDate) params.push(`start_date=${formatDateForAPI(startDate)}`);
        if (endDate) params.push(`end_date=${formatDateForAPI(endDate)}`);
        params.push('transaction_date_sort=asc');
        params.push(`page=${customPage}`);
        params.push(`limit=${customRowsPerPage}`);
        if (params.length) url += `?${params.join('&')}`;

        const response = await axiosInstance.get(url);

        setTotalCount(response.data.count || 0);
        const transformedTransactions = (response.data.results || []).map(transaction => {
          const formatDate = isoDate => {
            const date = new Date(isoDate);
            return date.toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
            });
          };
          const formatFullDate = isoDate => {
            const date = new Date(isoDate);
            return date.toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            });
          };
          return {
            id: transaction.id,
            transaction_id: transaction.transaction_id,
            start_date: transaction.start_date,
            end_date: transaction.end_date,
            transaction_date: transaction.transaction_date,
            total_payout_amount: transaction.total_payout_amount,
            bank_deduction_amount: transaction.bank_deduction_amount,
            actual_amount: transaction.actual_amount,
            created_at: transaction.created_at,
            updated_at: transaction.updated_at,
            updated_by: transaction.updated_by,
            dateRange: `${formatDate(transaction.start_date)} - ${formatDate(transaction.end_date)}`,
            date: formatFullDate(transaction.transaction_date),
            amount: transaction.total_payout_amount,
            bankDeduction: transaction.bank_deduction_amount,
            actualAmount: transaction.actual_amount,
          };
        });

        setTransactionHistory(transformedTransactions);
      } catch (error) {
        console.error('Error fetching transaction history:', error);

        if (error.response) {
          const status = error.response.status;
          if (status === 404) {
            setError('Organization not found');
          } else if (status === 401 || status === 403) {
            setError('You are not authorized to view this organization');
          } else {
            setError('Failed to load transaction history');
          }
        } else if (error.request) {
          setError('Network error. Please check your internet connection');
        } else {
          setError('An unexpected error occurred');
        }

        setTransactionHistory([]);
      } finally {
        setLoading(false);
      }
    },
    [organizationId, page, rowsPerPage]
  );

  // Handle filter card clicks
  const handleFilter = filter => {
    // Toggle off if clicking the same active filter
    if (activeFilter === filter) {
      setActiveFilter(null);
      setShowCalendar(false);
      return;
    }

    setActiveFilter(filter);

    // Open calendar when custom is selected
    if (filter === 'custom') {
      setShowCalendar(true);
    } else {
      setShowCalendar(false);
    }
  };

  // Fetch on mount and when filter or pagination changes
  useEffect(() => {
    // If no filter is active, fetch all without date constraints
    if (!activeFilter) {
      fetchTransactionHistory(undefined, undefined, page, rowsPerPage);
      return;
    }

    let startDate = null;
    let endDate = null;

    if (activeFilter === 'this-week') {
      startDate = getDateAgo('days', 7);
      endDate = new Date();
    } else if (activeFilter === 'this-month') {
      startDate = getDateAgo('months', 1);
      endDate = new Date();
    } else if (activeFilter === 'six-month') {
      startDate = getDateAgo('months', 6);
      endDate = new Date();
    } else if (activeFilter === 'one-year') {
      startDate = getDateAgo('years', 1);
      endDate = new Date();
    } else if (activeFilter === 'custom') {
      if (selectedDateRange.start && selectedDateRange.end) {
        startDate = selectedDateRange.start;
        endDate = selectedDateRange.end;
      } else {
        // Wait for both custom dates to be picked before fetching
        return;
      }
    }

    fetchTransactionHistory(startDate, endDate, page, rowsPerPage);
    // eslint-disable-next-line
  }, [activeFilter, selectedDateRange, organizationId, page, rowsPerPage]);

  // Refresh transaction history after adding/editing
  const refreshTransactionHistory = useCallback(() => {
    fetchTransactionHistory();
  }, [fetchTransactionHistory]);

  // Calendar helper functions
  const getDaysInMonth = date => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add days from previous month
    const prevMonth = new Date(year, month - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: daysInPrevMonth - i,
        date: new Date(year, month - 1, daysInPrevMonth - i),
        isCurrentMonth: false,
      });
    }

    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        date: new Date(year, month, i),
        isCurrentMonth: true,
      });
    }

    // Add days from next month to complete the grid
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
      });
    }

    return days;
  };

  const handleDateSelect = date => {
    if (isSelectingStart) {
      setSelectedDateRange({ start: date, end: null });
      setIsSelectingStart(false);
    } else {
      if (date >= selectedDateRange.start) {
        setSelectedDateRange({ ...selectedDateRange, end: date });
        setIsSelectingStart(true);
      } else {
        // If end date is before start date, swap them
        setSelectedDateRange({ start: date, end: selectedDateRange.start });
        setIsSelectingStart(true);
      }
    }
  };

  const handleConfirmDateRange = () => {
    if (selectedDateRange.start && selectedDateRange.end) {
      setShowCalendar(false);
      // Here you would typically filter your data based on the date range
      console.log('Date range selected:', selectedDateRange);
    }
  };

  const handleCancelDateRange = () => {
    setSelectedDateRange({ start: null, end: null });
    setIsSelectingStart(true);
    setShowCalendar(false);
  };

  const isDateInRange = date => {
    if (!selectedDateRange.start || !selectedDateRange.end) return false;
    return date >= selectedDateRange.start && date <= selectedDateRange.end;
  };

  const isDateSelected = date => {
    if (!selectedDateRange.start) return false;
    if (selectedDateRange.end) {
      return (
        date.getTime() === selectedDateRange.start.getTime() ||
        date.getTime() === selectedDateRange.end.getTime()
      );
    }
    return date.getTime() === selectedDateRange.start.getTime();
  };

  const handlePrevMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1));
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        showCalendar &&
        customButtonRef.current &&
        !customButtonRef.current.contains(event.target)
      ) {
        const calendarElement = document.querySelector('.org-custom-calendar');
        if (calendarElement && !calendarElement.contains(event.target)) {
          setShowCalendar(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  const handleEdit = transactionId => {
    if (!canManagePayouts) {
      toast.error("You don't have permission to manage payouts");
      return;
    }
    const transaction = transactionHistory.find(t => t.id === transactionId);
    if (transaction) {
      // Convert ISO date to DD-MM-YYYY format for the edit modal
      const formatDateForDisplay = isoDate => {
        if (!isoDate) return '';
        const date = new Date(isoDate);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };

      // Format the transaction data to match the expected format in the edit modal
      const formattedTransaction = {
        id: transaction.id,
        start_date: formatDateForDisplay(transaction.start_date),
        end_date: formatDateForDisplay(transaction.end_date),
        transaction_id: transaction.transaction_id,
        transaction_date: formatDateForDisplay(transaction.transaction_date),
        total_payout_amount: transaction.total_payout_amount,
        bank_deduction_amount: transaction.bank_deduction_amount,
        actual_amount: transaction.actual_amount,
      };
      setCurrentTransaction(formattedTransaction);
      setIsAddMode(false);
      setOpenEditModal(true);
    }
  };

  const handleAddNew = () => {
    if (!canManagePayouts) {
      toast.error("You don't have permission to manage payouts");
      return;
    }
    setCurrentTransaction(null); // No transaction data for add mode
    setIsAddMode(true);
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setCurrentTransaction(null);
  };

  const handleSaveTransaction = editedTransaction => {
    console.log('Save transaction:', editedTransaction, isAddMode ? 'Add new' : 'Edit');
    setOpenEditModal(false);
    // Refresh the transaction history after saving
    refreshTransactionHistory();
  };

  const handleDownload = event => {
    setAnchorEl(event.currentTarget);
    setOpenDatePicker(true);
  };

  const handleCloseDatePicker = () => {
    setOpenDatePicker(false);
    setAnchorEl(null);
  };

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  // Render custom calendar
  const renderCustomCalendar = () => {
    if (!showCalendar) return null;

    const days = getDaysInMonth(calendarDate);

    const calendar = (
      <div className="org-custom-calendar">
        <div className="org-calendar-header">
          <button onClick={handlePrevMonth} className="org-calendar-nav-btn">
            &lt;
          </button>
          <div className="org-calendar-title">
            {`${monthNames[calendarDate.getMonth()]} ${calendarDate.getFullYear()}`}
          </div>
          <button onClick={handleNextMonth} className="org-calendar-nav-btn">
            &gt;
          </button>
        </div>

        <div className="org-calendar-days-header">
          <div>S</div>
          <div>M</div>
          <div>T</div>
          <div>W</div>
          <div>T</div>
          <div>F</div>
          <div>S</div>
        </div>

        <div className="org-calendar-grid">
          {days.map((day, index) => (
            <div
              key={index}
              className={`org-calendar-day ${!day.isCurrentMonth ? 'other-month' : ''}
                         ${isDateSelected(day.date) ? 'selected' : ''}
                         ${isDateInRange(day.date) ? 'in-range' : ''}`}
              onClick={() =>
                setCanManagePayouts
                  ? handleDateSelect(day.date)
                  : toast.error("You don't have permission to manage payouts")
              }
            >
              {day.day}
            </div>
          ))}
        </div>

        <div className="org-calendar-actions">
          <button onClick={handleCancelDateRange} className="org-calendar-cancel-btn">
            Cancel
          </button>
          <button
            onClick={handleConfirmDateRange}
            className="org-calendar-confirm-btn"
            disabled={!selectedDateRange.start || !selectedDateRange.end}
          >
            Confirm
          </button>
        </div>
      </div>
    );

    return ReactDOM.createPortal(calendar, document.body);
  };

  // Pagination logic
  const { items } = usePagination({
    count: Math.ceil(totalCount / rowsPerPage) || 1,
    page,
    onChange: (event, value) => setPage(value),
  });
  const handleRowsPerPageChange = event => {
    setRowsPerPage(event.target.value);
    setPage(1);
  };

  return (
    <Box className="org-payout-container">
      {/* Payout History Header */}
      <Box className="org-payout-header">
        <Typography className="org-payout-title">Payout History</Typography>
        <Box className="org-payout-filter-container">
          <Box className="org-payout-period-filter">
            <button
              className={`org-period-filter-btn ${activeFilter === 'this-week' ? 'org-active' : ''}`}
              onClick={() => handleFilter('this-week')}
            >
              This Week
            </button>
            <button
              className={`org-period-filter-btn ${activeFilter === 'this-month' ? 'org-active' : ''}`}
              onClick={() => handleFilter('this-month')}
            >
              This Month
            </button>
            <button
              className={`org-period-filter-btn ${activeFilter === 'six-month' ? 'org-active' : ''}`}
              onClick={() => handleFilter('six-month')}
            >
              6 Month
            </button>
            <button
              className={`org-period-filter-btn ${activeFilter === 'one-year' ? 'org-active' : ''}`}
              onClick={() => handleFilter('one-year')}
            >
              1 Year
            </button>
            <button
              ref={customButtonRef}
              className={`org-period-filter-btn ${activeFilter === 'custom' ? 'org-active' : ''}`}
              onClick={() => handleFilter('custom')}
            >
              Custom
            </button>
          </Box>
          <Button
            variant="contained"
            color="primary"
            className="org-add-payout-btn"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            disabled={loading}
          >
            Add
          </Button>
        </Box>
      </Box>

      {/* Transaction History Table */}
      <Box className="org-transaction-history-container">
        {loading ? (
          <PayoutTableSkeleton />
        ) : error ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
            }}
          >
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <Box className="org-transaction-table-container">
            <table className="org-transaction-table">
              <thead>
                <tr>
                  <th className="org-payout-th">Date Range</th>
                  <th className="org-payout-th">Transaction ID</th>
                  <th className="org-payout-th">Transaction Date</th>
                  <th className="org-payout-th">Total Earning (Riyals)</th>
                  <th className="org-payout-th">Bank Deduction (Riyals)</th>
                  <th className="org-payout-th">Actual Amount (Riyals)</th>
                  <th className="org-payout-th center">Action</th>
                </tr>
              </thead>
              <tbody>
                {transactionHistory.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="org-payout-empty-cell">
                      <Box className="org-payout-empty-state">
                        <Typography className="org-payout-empty-text">
                          No transactions found
                        </Typography>
                      </Box>
                    </td>
                  </tr>
                ) : (
                  transactionHistory.map(transaction => (
                    <tr key={transaction.id} className="org-payout-row">
                      <td className="org-payout-td">
                        <Typography className="org-transaction-date-range">
                          {transaction.dateRange}
                        </Typography>
                      </td>
                      <td className="org-payout-td">
                        <Typography className="org-transaction-id">
                          {transaction.transaction_id}
                        </Typography>
                      </td>
                      <td className="org-payout-td">
                        <Typography className="org-transaction-date">{transaction.date}</Typography>
                      </td>
                      <td className="org-payout-td">
                        <Typography className="org-transaction-amount">
                          {transaction.amount}
                        </Typography>
                      </td>
                      <td className="org-payout-td">
                        <Typography className="org-bank-deduction">
                          {transaction.bankDeduction}
                        </Typography>
                      </td>
                      <td className="org-payout-td">
                        <Typography className="org-actual-amount">
                          {transaction.actualAmount}
                        </Typography>
                      </td>
                      <td className="org-payout-td org-action-cell center">
                        <Button
                          variant="text"
                          className="org-edit-button"
                          onClick={() => handleEdit(transaction.id)}
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>
        )}
      </Box>

      {/* Edit Transaction Modal */}
      <EditTransactionHistory
        open={openEditModal}
        handleClose={handleCloseEditModal}
        transaction={currentTransaction}
        organizationId={organizationId}
        onSave={handleSaveTransaction}
        canManagePayouts={canManagePayouts}
      />

      {/* Render custom calendar */}
      {renderCustomCalendar()}

      {/* Pagination Footer */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2,
          px: 2,
          pb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Typography
            sx={{ fontFamily: 'Poppins', fontSize: '12px', fontWeight: 400, color: '#000' }}
          >
            Showing
          </Typography>
          <Select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            sx={{
              height: '32px',
              minWidth: '52px',
              fontFamily: 'Poppins',
              fontSize: '12px',
              fontWeight: 400,
              color: '#000',
              '& .MuiSelect-select': {
                padding: '6px 12px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #d5dee3',
              },
              '& .MuiMenuItem-root': {
                fontFamily: 'Poppins',
                fontSize: '12px',
                fontWeight: 400,
                color: '#000',
              },
            }}
          >
            {[10, 15, 20, 30].map(num => (
              <MenuItem key={num} value={num}>
                {num}
              </MenuItem>
            ))}
          </Select>
          <Typography
            sx={{ fontFamily: 'Poppins', fontSize: '12px', fontWeight: 400, color: '#000' }}
          >
            {Math.min(rowsPerPage, Math.max(0, totalCount - (page - 1) * rowsPerPage))} of{' '}
            {totalCount || 0} transactions
          </Typography>
        </Box>
        <nav>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
            }}
          >
            {items.map(({ page: itemPage, type, selected, ...item }, index) => {
              if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                return (
                  <li
                    key={index}
                    style={{
                      fontFamily: 'Poppins',
                      fontSize: '12px',
                      fontWeight: 400,
                      color: '#000',
                    }}
                  >
                    …
                  </li>
                );
              }

              const isPage = type === 'page';
              return (
                <li key={index}>
                  <button
                    type="button"
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: `1px solid ${selected ? '#0a5ef3' : '#d5dee3'}`,
                      backgroundColor: selected ? '#0a5ef3' : '#fff',
                      color: selected ? '#fff' : '#000',
                      fontFamily: 'Poppins',
                      fontSize: '12px',
                      fontWeight: 400,
                      cursor: item.disabled ? 'not-allowed' : 'pointer',
                      opacity: item.disabled ? 0.5 : 1,
                      minWidth: isPage ? '32px' : 'auto',
                    }}
                    {...item}
                  >
                    {type === 'previous' && 'Previous'}
                    {type === 'next' && 'Next'}
                    {isPage && itemPage}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </Box>
    </Box>
  );
};

export default OrganizationPayout;
